import { Bundle, CategoryDocument, categorySchema } from '@models/category';
import { db } from '@util/firebase';
import { client } from '@util/getTypesense';
import { logError } from '@util/logError';
import { slugify } from '@util/urlHelpers';
import {
  collection,
  collectionGroup,
  CollectionReference,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { getStorage, ref, uploadString } from 'firebase/storage';
import { RideType, rideTypes } from 'models/shared';
import { OtherDocDocument } from 'models/trending-search';
const storage = getStorage();

const newColRef = collection(
  db,
  'categories_new'
) as CollectionReference<CategoryDocument>;

export async function getSubcatsClient(
  level: 0 | 1 | 2 | 3 | 4,
  id?: string,
  omit_seo?: boolean,
  firestore?: boolean
) {
  if (!id) return [];
  const path = `${id}/category${level}_new`;
  if (firestore) {
    const q = query(collection(db, path));
    const { docs } = await getDocs(q);
    return docs.map((doc) => doc.data() as CategoryDocument);
  }
  const res = await fetch('/api/categories-bundle');
  if (!res.ok) throw new Error('Failed to fetch categories from json bundle');
  const json = await res.json();
  const bundle = json as {
    'categories_new-query': CategoryDocument[];
    'category1_new-query': CategoryDocument[];
    'category2_new-query': CategoryDocument[];
    'category3_new-query': CategoryDocument[];
  };
  const allCats = [
    ...bundle['categories_new-query'],
    ...bundle['category1_new-query'],
    ...bundle['category2_new-query'],
    ...bundle['category3_new-query'],
  ];
  return allCats.filter(
    (cat) =>
      cat.id.startsWith(path) &&
      (!omit_seo || !cat.is_seo_only) &&
      cat.cat_level === level
  );
}

export async function getCategoryByID(id: string) {
  const docRef = doc(db, id);
  const docSnap = await getDoc(docRef);
  return docSnap.data() as CategoryDocument;
}

export async function uploadCatImage(localUri: string, slug: string) {
  const path = `categories/${slug}-${Date.now()}`;
  const storeImageRef = ref(storage, path);
  const res = await uploadString(storeImageRef, localUri, 'data_url');
  return res.metadata.fullPath;
}
export const getCatID = (
  parent_id: string,
  name: string,
  cat_level: number
) => {
  if (cat_level === 0) return `categories_new/${slugify(name)}`;
  return parent_id + `/category${cat_level}_new/` + slugify(name);
};

export function setCat(
  parent_id: string,
  cat: CategoryDocument,
  isEdit?: boolean
) {
  if (!isEdit) {
    cat.id = getCatID(parent_id, cat.name, cat.cat_level);
  }
  const parsedCat = categorySchema.parse(cat);
  const docRef = doc(db, parsedCat.id);
  return setDoc(docRef, parsedCat);
}

export function deleteCategory(cat: CategoryDocument) {
  const docRef = doc(db, cat.id);
  return deleteDoc(docRef);
}

export async function getCatBundleClient() {
  const res = await fetch('/api/categories-bundle');
  if (!res.ok) throw new Error('Failed to fetch categories from json bundle');
  const json = await res.json();
  return json as Bundle;
}

export async function getMainCatsClient(firestore?: boolean) {
  if (firestore) {
    const q = query(collection(db, 'categories_new'));
    const { docs } = await getDocs(q);
    return docs.map((doc) => doc.data() as CategoryDocument);
  }
  const bundle = await getCatBundleClient();
  return bundle['categories_new-query'];
}

export async function getCat1sClient(parent_id: string) {
  try {
    const bundle = await getCatBundleClient();
    return bundle['category1_new-query'].filter((cat) =>
      cat.id.startsWith(parent_id)
    );
  } catch (e) {
    logError(e);
    return [];
  }
}

export async function getCat2sClient(parent_id: string) {
  try {
    const bundle = await getCatBundleClient();
    return bundle['category2_new-query'].filter((cat) =>
      cat.id.startsWith(parent_id)
    );
  } catch (e) {
    logError(e);
    return [];
  }
}

export async function getCat3sClient(parent_id: string) {
  try {
    const bundle = await getCatBundleClient();
    return bundle['category3_new-query'].filter((cat) =>
      cat.id.startsWith(parent_id)
    );
  } catch (e) {
    logError(e);
    return [];
  }
}

export const CATEGORIES = [
  'Riding Gear',
  'Bike Parts',
  'Accessories',
  'Bikes',
] as const;

export const SPORTS = [
  'Dirt Bike',
  'ATV',
  'UTV',
  'Street',
  'Cycling',
  'Snowmobile',
  'Casual',
  'Collectibles',
] as const;

export const CYCLING_SPORTS = [
  'Mountain Bikes',
  'Road Bikes',
  'E-Bikes',
] as const;

export const STREET_SPORTS = ['Adventure', 'Cruiser', 'Sport'] as const;

export const NON_SPORT_MAP = {
  '/shop/riding-gear/gear':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/gear',
  '/shop/riding-gear/boots':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/boots',
  '/shop/riding-gear/helmets':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/helmets',
  '/shop/riding-gear/protective':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/protective',
  '/shop/riding-gear/goggles-accessories':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/goggles-accessories',
  '/shop/gear/gloves':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/gear/category3_new/gloves',
  '/shop/gear/gear-combos':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/gear/category3_new/gear-combos',
  '/shop/gear/jerseys':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/gear/category3_new/jerseys',
  '/shop/gear/pants':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/gear/category3_new/pants',
  '/shop/goggles-accessories/goggles':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/goggles-accessories/category3_new/goggles',
  '/shop/goggles-accessories/lenses':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/goggles-accessories/category3_new/lenses',
  '/shop/goggles-accessories/tear-offs':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/goggles-accessories/category3_new/tear-offs',
  '/shop/goggles-accessories/cases':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/goggles-accessories/category3_new/cases',
  '/shop/protective/chest-protectors':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/protective/category3_new/chest-protectors',
  '/shop/protective/knee-braces':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/protective/category3_new/knee-braces',
  '/shop/protective/neck-braces':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/protective/category3_new/neck-braces',
  '/shop/protective/other':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/protective/category3_new/other',
  '/shop/protective/shorts-baselayers':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/protective/category3_new/shorts-baselayers',
  '/shop/protective/wrist-braces':
    'categories_new/dirt-bikes/category1_new/riding-gear/category2_new/protective/category3_new/wrist-braces',
  '/shop/motorcycle-parts':
    'categories_new/street/category1_new/sport/category2_new/bike-parts',
  '/shop/motorcycle-riding-gear':
    'categories_new/street/category1_new/sport/category2_new/riding-gear',
};

export function getRideTypes(cat: CategoryDocument): RideType[] {
  let ride_types: RideType[] = [];
  const first_id = cat.id.split('/')[1];
  const second_id = cat.id.split('/')[3];
  if (first_id.includes('dirt-bikes')) ride_types.push('Dirt Bikes');
  if (first_id.includes('atv')) ride_types.push('ATV');
  if (first_id.includes('utv')) ride_types.push('UTV');
  if (first_id.includes('snowmobiles')) ride_types.push('Snow');
  if (first_id.includes('street')) ride_types.push('Street');
  if (second_id?.includes('adventure')) ride_types.push('Adventure');
  if (second_id?.includes('sport')) ride_types.push('Sport');
  if (second_id?.includes('cruiser')) ride_types.push('Cruiser');
  if (first_id.includes('cycling')) ride_types.push('Cycling');
  if (second_id?.includes('mountain-bikes')) ride_types.push('Mountain Bikes');
  if (second_id?.includes('e-bikes')) ride_types.push('E-Bikes');
  if (second_id?.includes('road-bikes')) ride_types.push('Road Bikes');
  if (first_id.includes('collectibles')) ride_types.push('Dirt Bikes');
  return ride_types;
}

export function getFullSlugfromCategoryId({
  id,
  isMasterProduct,
}: {
  id: string;
  isMasterProduct?: boolean;
}) {
  const base = '/shop';
  const parts = id.split('/');
  // 'categories_new/atv/category1_new/atv-parts' -> atv-parts
  let sport = parts[1];
  let cat1 = parts[3];
  let cat2 = parts[5];
  let cat3 = parts[7];
  let cat4 = parts[9];
  if (cat4) {
    if (['dirt-bikes', 'atv', 'utv', 'snowmobiles'].includes(sport)) {
      // /shop/cat1/cat4
      return `${base}/${cat1}/${cat4}`;
    } else {
      // /shop/sport/cat1-cat4
      if (cat1.endsWith('s')) {
        return `${base}/${sport}/${cat1.slice(0, -1)}-${cat4}`;
      }
      return `${base}/${sport}/${cat1}-${cat4}`;
    }
  } else if (cat3) {
    if (['dirt-bikes', 'atv', 'utv', 'snowmobiles'].includes(sport)) {
      if (isMasterProduct) {
        // shop/cat1/cat3
        return `${base}/${cat1}/${cat3}`;
      } else {
        // /shop/cat1/cat2/cat3
        if (cat1.includes('parts') || cat1.includes('accessories')) {
          if (sport.endsWith('s')) {
            return `${base}/${sport.slice(0, -1)}-${cat1}/${cat2}/${cat3}`;
          } else {
            return `${base}/${sport}-${cat1}/${cat2}/${cat3}`;
          }
        } else if (cat1.includes('riding-gear')) {
          if (sport === 'dirt-bikes') {
            return `${base}/${cat1}/dirt-bike-and-motocross-${cat3}`;
          }
          if (sport.endsWith('s')) {
            return `${base}/${cat1}/${sport.slice(0, -1)}-${cat3}`;
          }
          return `${base}/${cat1}/${sport}-${cat3}`;
        }
      }
    } else if (cat1.includes('parts') && ['street'].includes('sport')) {
      return `${base}/motorcycle-parts/${cat2}/${cat3}`;
    } else {
      if (cat2 === 'riding-gear') {
        if (
          STREET_SPORTS.map((s) => slugify(s)).includes(
            cat1 as (typeof STREET_SPORTS)[number]
          )
        ) {
          return `${base}/motorcycle/${cat1}-${cat3}`;
        }
        return `${base}/${sport}/${cat1}-${cat3}`;
      } else {
        if (cat2.endsWith('bikes')) {
          return `${base}/${cat2}/${cat3}`;
        }
        // /shop/cat1-cat2/cat3
        if (cat1.endsWith('s')) {
          cat2 = cat2.replace(cat1.slice(0, -1) + '-', '');
          return `${base}/${cat1.slice(0, -1)}-${cat2}/${cat3}`;
        }
        cat2 = cat2.replace(cat1 + '-', '');
        return `${base}/${cat1}-${cat2}/${cat3}`;
      }
    }
  } else if (cat2) {
    if (['dirt-bikes', 'atv', 'utv', 'snowmobiles'].includes(sport)) {
      if (cat1.includes('parts')) {
        return `${base}/${cat1}/${cat2}`;
      } else if (cat1.includes('accessories')) {
        if (sport.endsWith('s')) {
          return `${base}/${sport.slice(0, -1)}-${cat1}/${cat2}`;
        }
        return `${base}/${sport}-${cat1}/${cat2}`;
      } else if (cat1.includes('riding-gear')) {
        if (sport === 'dirt-bikes') {
          return `${base}/${cat1}/dirt-bike-and-motocross-${cat2}`;
        }
        if (sport.endsWith('s')) {
          return `${base}/${cat1}/${sport.slice(0, -1)}-${cat2}`;
        }
        return `${base}/${cat1}/${sport}-${cat2}`;
      }
    } else if (cat1.includes('parts') && ['street'].includes(sport)) {
      return `${base}/motorcycle-parts/${cat2}`;
    } else {
      if (cat2.endsWith('bikes')) {
        return `${base}/${cat2}`;
      }
      if (cat1.endsWith('s')) {
        return `${base}/${cat1.slice(0, -1)}-${cat2.replace(
          cat1.slice(0, -1) + '-',
          ''
        )}`;
      }
      return `${base}/${cat1}-${cat2.replace(cat1 + '-', '')}`;
    }
  } else if (cat1) {
    if (sport === 'casual') {
      return `${base}/${sport}/${cat1}`;
    }
    if (sport === 'dirt-bikes' || sport === 'snowmobiles') {
      if (cat1.includes(sport.slice(0, -1))) {
        return `${base}/${cat1}`;
      }
      return `${base}/${sport.slice(0, -1)}-${cat1}`;
    }
    if (cat1.includes(sport)) {
      return `${base}/${cat1}`;
    }
    if (sport === 'street') {
      return `${base}/motorcycle/${cat1}`;
    }
    return `${base}/${sport}-${cat1}`;
  } else if (sport) {
    return `${base}/${sport}`;
  }
  return `${base}`;
}

export function getProductMap(
  breadcrumbs: CategoryDocument['breadcrumbs'],
  id: string
): CategoryDocument['product_cats'] | null {
  if (!breadcrumbs.length) {
    console.error('Empty breadcrumbs id: ', id);
    return null;
  }
  const name =
    breadcrumbs[0].name === 'Motorcycle'
      ? 'Street'
      : breadcrumbs[0].name === 'Snowmobiles'
      ? 'Snow'
      : breadcrumbs[0].name;

  if (name === 'Casual' || name === 'Collectibles') {
    if (name === 'Casual' && breadcrumbs[1])
      return {
        category: name,
        category1: breadcrumbs[1].name,
        ride_type: null,
        category2: null,
        category3: null,
        category4: null,
      };
    return {
      category: name,
      ride_type: null,
      category1: null,
      category2: null,
      category3: null,
      category4: null,
    };
  }
  const parse = rideTypes.safeParse(name);
  if (!parse.success) {
    console.error('Failed to parse main id: ', name, id);
    return null;
  }
  const mainCat = parse.data;
  const obj = breadcrumbs.reduce((acc, cur, i) => {
    if (i === 0) acc.ride_type = mainCat;
    else if (i === 1) {
      if (cur.name.includes('Parts')) acc.category = 'Bike Parts';
      else if (cur.name.includes('Accessories')) acc.category = 'Accessories';
      else if (cur.name.includes('Gear') || cur.name.includes('Apparel'))
        acc.category = 'Riding Gear';
    } else if (i === 2) acc.category1 = cur.name;
    else if (i === 3) acc.category2 = cur.name;
    else if (i === 4) acc.category3 = cur.name;
    return acc;
  }, {} as CategoryDocument['product_cats']);
  return obj;
}

export function getLastProductCat(cat: CategoryDocument) {
  if (cat.product_cats.category4) return cat.product_cats.category4;
  if (cat.product_cats.category3) return cat.product_cats.category3;
  if (cat.product_cats.category2) return cat.product_cats.category2;
  if (cat.product_cats.category1) return cat.product_cats.category1;
  return cat.product_cats.category ?? cat.name;
}

// data is in homepage bundle
export async function getTrendingSearches() {
  const docSnap = await getDoc(doc(db, 'other', 'trending_searches'));
  const docData = docSnap.data() as OtherDocDocument;
  return docData.trending_searches?.terms ?? [];
}

// Legacy cats for dirt bike for sale pages
export async function getMakeCategory(make?: string) {
  if (!make) {
    // get main dirt bikes category from old categories colleciton
    const q = query(
      collection(db, 'categories'),
      where('name', '==', 'Dirt Bikes')
    );
    const { docs } = await getDocs(q);
    return docs.map((doc) => doc.data() as CategoryDocument)[0];
  }
  // use old category2 collection group
  const q = query(collectionGroup(db, 'category2'), where('name', '==', make));
  const { docs } = await getDocs(q);
  return docs.map((doc) => doc.data() as CategoryDocument)[0];
}

export async function searchCategories(
  search?: string,
  ride_type?: RideType | null
) {
  const res = await client
    .collections<CategoryDocument>('categories_alias')
    .documents()
    .search({
      q: search ?? '*',
      query_by: 'name',
      filter_by: ride_type ? `product_cats.ride_type:=${ride_type}` : '',
      per_page: 10,
    });
  return res.hits ?? [];
}
